import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiUploadCloud } from "react-icons/fi";
import useJobStore from "../store/jobStore";

const FileUploadPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { style } = useParams<{ style: string }>(); // Get style from URL
  const [originalImageDataUrl, setOriginalImageDataUrl] = useState<
    string | null
  >(null); // State for original image data URL

  const {
    jobId, // Needed to know when to redirect
    error,
    isUploading,
    startUpload,
    setError,
    resetJobState, // Keep reset available if needed
  } = useJobStore();

  // Redirect to style selection if no style is provided in the URL
  useEffect(() => {
    if (!style) {
      console.warn("No style provided, redirecting to style selection.");
      navigate("/"); // Or wherever style selection lives
    }
  }, [style, navigate, resetJobState]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setOriginalImageDataUrl(null); // Reset preview on new selection

    if (!file) {
      setError(null); // Clear previous errors on new selection attempt
      return;
    }

    // Validate file type
    if (!file.type.match("image.*")) {
      setError(t("homePage.validation.selectImageFile"));
      return;
    }

    setError(null); // Clear any previous errors

    window.trackEvent?.("file_selected", {
      fileType: file.type,
      fileSize: file.size,
      style: style || "",
    });

    // Create a preview URL and update the store
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      setOriginalImageDataUrl(dataUrl); // Store the data URL

      // Automatically start upload after preview is set, if style is valid
      if (style && file) {
        startUpload(file, style); // Call startUpload with the file and style
        window.trackEvent?.("upload_started", { style });
      } else {
        // This case should ideally not happen if style is checked earlier
        setError(t("errors.styleRequired", "Style is required to upload."));
        console.error("Style or file missing for upload start.");
      }
    };
    reader.readAsDataURL(file);
  };

  // Redirect to results page when jobId is set and upload potentially started
  // We navigate immediately on jobId, the results page will handle polling/status updates
  useEffect(() => {
    if (jobId) {
      // Pass the original image data URL in the navigation state
      navigate(`/results`, { state: { originalImage: originalImageDataUrl } });
    }
  }, [jobId, navigate, originalImageDataUrl]); // Add originalImageDataUrl to dependency array

  return (
    <div className="w-full max-w-4xl mx-auto py-12 px-4 text-center">
      <h2 className="text-3xl font-semibold mb-2">
        {t("fileUploadPage.title", "Upload Your Image")}
      </h2>

      {/* Display errors from the store */}
      {error && (
        <div className="mb-6 p-4 bg-red-900 bg-opacity-50 text-red-300 rounded-lg border border-red-800">
          {t(error, error)}
        </div>
      )}

      {/* Show Uploading state */}
      {isUploading && (
        <div className="flex items-center justify-center text-gray-400 my-10">
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {t("homePage.upload.uploadingButton")}{" "}
          {/* Reusing existing translation key */}
        </div>
      )}

      {/* Show File Upload Area only if not currently uploading */}
      {!isUploading && (
        <div className="mb-8">
          <label
            htmlFor="image-upload"
            className="block w-full p-8 border-2 border-dashed border-gray-700 rounded-lg cursor-pointer hover:border-gray-500 transition-colors duration-300 bg-gray-800 bg-opacity-30 hover:bg-opacity-40"
          >
            <div className="text-gray-400 flex flex-col items-center justify-center">
              <FiUploadCloud className="w-16 h-16 mb-4 text-gray-500" />
              <p className="text-lg">
                {t("fileUploadPage.prompt", "Click to select an image")}
              </p>{" "}
            </div>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading} // Disable input while uploading
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default FileUploadPage;
